import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { OLITASK_SERVICE_BASE_URL } from "constants";
import apiClient from "utils/apiClient";

const AIRPORTS_API_BASE_URL = `${OLITASK_SERVICE_BASE_URL}/api/v1/airports`;
const AIRPORT_TOAST_CONFIG = {
  theme: "colored",
  style: { zIndex: 100100 },
};

const normalizeAirportRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const extractErrorMessage = (err, action = "operation") => {
  const fallback = `Failed to ${action}.`;
  const data = err?.response?.data;

  if (!data) {
    if (err?.request) {
      return "No response from the server. Please check your internet connection.";
    }
    return err?.message || fallback;
  }

  if (typeof data === "string" && data.trim()) {
    return data;
  }

  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    const collected = data.errors
      .map((entry) => {
        if (typeof entry === "string") return entry;
        const field = entry?.field || entry?.name;
        const message = entry?.defaultMessage || entry?.message || entry?.error;
        if (!message) return null;
        return field ? `${field}: ${message}` : message;
      })
      .filter(Boolean);
    if (collected.length > 0) return collected.join("; ");
  }

  if (data?.errors && typeof data.errors === "object") {
    const collected = Object.entries(data.errors)
      .flatMap(([field, value]) => {
        if (Array.isArray(value)) {
          return value.map((message) => `${field}: ${message}`);
        }
        if (typeof value === "string") {
          return [`${field}: ${value}`];
        }
        return [];
      })
      .filter(Boolean);
    if (collected.length > 0) return collected.join("; ");
  }

  return (
    data?.message ||
    data?.error_description ||
    data?.detail ||
    data?.error ||
    err?.message ||
    fallback
  );
};

export default function useAirports(page = 0, pageSize = 20) {
  const authToken = sessionStorage.getItem("authToken");
  const [airports, setAirports] = useState([]);
  const [selectedAirport, setSelectedAirport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  const handleAxiosErrors = (err, action = "operation") => {
    const message = extractErrorMessage(err, action);
    const normalizedError = new Error(message);
    normalizedError.raw = err;
    normalizedError.status = err?.response?.status;

    setError(normalizedError);
    toast.error(message, AIRPORT_TOAST_CONFIG);
    console.error(`${action} error:`, err);
    return normalizedError;
  };

  const fetchAirports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(
        `${AIRPORTS_API_BASE_URL}?page=${page}&size=${pageSize}`,
      );
      const payload = response.data;
      const rows = normalizeAirportRows(payload);

      setAirports(rows);
      setTotalPages(Array.isArray(payload) ? 1 : payload?.totalPages || 1);
      setTotalElements(
        Array.isArray(payload) ? rows.length : payload?.totalElements || rows.length,
      );
      return rows;
    } catch (err) {
      handleAxiosErrors(err, "fetch airports");
      return [];
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  const fetchAirportById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`${AIRPORTS_API_BASE_URL}/${id}`);
      setSelectedAirport(response.data);
      return response.data;
    } catch (err) {
      throw handleAxiosErrors(err, "fetch airport details");
    } finally {
      setLoading(false);
    }
  }, []);

  const createAirport = useCallback(async (airportData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post(AIRPORTS_API_BASE_URL, airportData);
      const created = response.data;
      setAirports((prev) => [created, ...(Array.isArray(prev) ? prev : [])]);
      setTotalElements((prev) => prev + 1);
      setSelectedAirport(created);

      // Refresh to keep list aligned with server pagination/sorting.
      const refreshedRows = await fetchAirports();
      if (
        page === 0 &&
        created?.id &&
        Array.isArray(refreshedRows) &&
        !refreshedRows.some((row) => row?.id === created.id)
      ) {
        // Keep the just-created item visible on the first page even if backend ordering places it elsewhere.
        setAirports([created, ...refreshedRows].slice(0, pageSize));
      }
      toast.success("Airport created successfully!", AIRPORT_TOAST_CONFIG);
      return response.data;
    } catch (err) {
      throw handleAxiosErrors(err, "create airport");
    } finally {
      setLoading(false);
    }
  }, [fetchAirports]);

  const updateAirport = useCallback(async (id, airportData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.put(
        `${AIRPORTS_API_BASE_URL}/${id}`,
        airportData,
      );
      const updated = response.data;

      setAirports((prev) =>
        (Array.isArray(prev) ? prev : []).map((airport) =>
          airport.id === id ? updated : airport,
        ),
      );
      setSelectedAirport((prev) =>
        prev && prev.id === id ? updated : prev,
      );

      // Refresh to keep list aligned with server pagination/sorting.
      await fetchAirports();
      toast.success("Airport updated successfully!", AIRPORT_TOAST_CONFIG);
      return updated;
    } catch (err) {
      throw handleAxiosErrors(err, "update airport");
    } finally {
      setLoading(false);
    }
  }, [fetchAirports]);

  const deleteAirport = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      await apiClient.delete(`${AIRPORTS_API_BASE_URL}/${id}`);
      setAirports((prev) =>
        (Array.isArray(prev) ? prev : []).filter((airport) => airport.id !== id),
      );
      setTotalElements((prev) => Math.max(prev - 1, 0));
      setSelectedAirport((prev) => (prev?.id === id ? null : prev));

      // Refresh to keep list aligned with server pagination/sorting.
      await fetchAirports();
      toast.success("Airport deleted successfully!", AIRPORT_TOAST_CONFIG);
    } catch (err) {
      throw handleAxiosErrors(err, "delete airport");
    } finally {
      setLoading(false);
    }
  }, [fetchAirports]);

  useEffect(() => {
    if (authToken) {
      fetchAirports();
    }
  }, [authToken, fetchAirports]);

  return {
    airports,
    selectedAirport,
    loading,
    error,
    totalPages,
    totalElements,
    fetchAirports,
    fetchAirportById,
    createAirport,
    updateAirport,
    deleteAirport,
    setSelectedAirport,
  };
}
